import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { ssnNumberMask } from '../../../utils/utils';
import GenericCard from '../../../components/GenericCards/';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import CheckboxGroup from '../../../components/Checkbox/CheckboxGroup';
import ConfirmChangesModal from '../ConfirmChangesModal';
import useModal from '../../../hooks/useModal';

export type StepProps = {
  data: any;
  collectStepData: (data: any) => void;
  goToStep: (step: number) => void;
};

const VerifyDependentStep4 = ({
  data,
  collectStepData,
  goToStep,
}: StepProps) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [
    showConfirmChangesModal,
    handleShowConfirmChangesModal,
    handleCloseConfirmChangesModal,
  ] = useModal();

  const handleOnTermsCheckboxValueChange = (event: any, values: string[]) => {
    collectStepData({
      ...data,
      hasAccepted: values[1],
    });
  };

  const setStep = (step: number) => {
    setCurrentStep(step);
    handleShowConfirmChangesModal();
  };

  return (
    <Fragment>
      <ConfirmChangesModal
        currentStep={currentStep}
        showConfirmChangesModal={showConfirmChangesModal}
        handleCloseConfirmChangesModal={handleCloseConfirmChangesModal}
        goToStep={() => goToStep(currentStep)}
      />
      <section className={'negative-margin-mobile mt-11 pt-2 pt-lg-1 px-0'}>
        <div className={'px-lg-13 px-7 py-10'}>
          <h5 className={'text-fs-base text-lg-center fw-normal mb-0'}>
            {
              'Please review all responses below. If they are not accurate, please correct them'
            }
          </h5>
        </div>
        <div className={'mt-3 mx-7 mx-lg-auto'} style={{ maxWidth: '688px' }}>
          <GenericCard variant={'white'} className={'p-0'}>
            <div className={'card-item p-10'}>
              <div className={'d-flex justify-content-between'}>
                <h3 className={'h6'}>{'Relationship'}</h3>
                <Button
                  leftIcon={light('pen-to-square')}
                  size={'sm'}
                  onClick={() => setStep(0)}
                >
                  {'Edit'}
                </Button>
              </div>
              <Row className={'mt-3'}>
                <Col className={'col-6 col-lg-3'}>
                  <div className={'text-caption text-ligther-grey fw-light'}>
                    {'Relationship Status'}
                  </div>
                  <div className={'mt-1 text-fs-base fw-normal'}>
                    {'Biological Child'}
                  </div>
                </Col>
                <Col className={'col-6 col-lg-3'}>
                  <div className={'text-caption text-ligther-grey fw-light'}>
                    {'Dependent Disabled'}
                  </div>
                  <div className={'mt-1 text-fs-base fw-normal'}>{'No'}</div>
                </Col>
                <Col className={'col-12 col-lg-3 mt-10 mt-lg-0'}>
                  <div className={'text-caption text-ligther-grey fw-light'}>
                    {'Full-time Student'}
                  </div>
                  <div className={'mt-1 text-fs-base fw-normal'}>{'No'}</div>
                </Col>
              </Row>
            </div>
          </GenericCard>

          <GenericCard variant={'white'} className={'p-0 mt-7'}>
            <div className={'card-item p-10'}>
              <div className={'d-flex justify-content-between'}>
                <h3 className={'h6'}>{'Documentation'}</h3>
                <Button
                  leftIcon={light('pen-to-square')}
                  size={'sm'}
                  onClick={() => setStep(1)}
                >
                  {'Edit'}
                </Button>
              </div>
              <Row className={'mt-3'}>
                <Col className={'col-6 col-lg-3'}>
                  <div className={'text-caption text-ligther-grey fw-light'}>
                    {'Birth Certificate'}
                  </div>
                  <div className={'mt-1 text-fs-base fw-normal'}>
                    {'File Upload'}
                  </div>
                </Col>
                <Col className={'col-6 col-lg-3 align-self-end'}>
                  <div className={'text-caption text-info fw-light'}>
                    {'filename.pdf'}
                    <FontAwesomeIcon
                      icon={light('paperclip')}
                      className={'ms-3'}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </GenericCard>

          <GenericCard variant={'white'} className={'p-0 mt-7'}>
            <div className={'card-item p-10'}>
              <div className={'d-flex justify-content-between'}>
                <h3 className={'h6'}>{'Social Security Number'}</h3>
                <Button
                  leftIcon={light('pen-to-square')}
                  size={'sm'}
                  onClick={() => setStep(2)}
                >
                  {'Edit'}
                </Button>
              </div>
              <Row className={'mt-3'}>
                <Col className={'col-6 col-lg-3'}>
                  <div className={'text-caption text-ligther-grey fw-light'}>
                    {'SSN'}
                  </div>
                  <div className={'mt-1 text-fs-base fw-normal'}>
                    {ssnNumberMask('123456789')}
                  </div>
                </Col>
              </Row>
            </div>
          </GenericCard>
        </div>
        <div className={'mt-11 mt-lg-13 px-lg-13 pb-13 px-12'}>
          <CheckboxGroup
            name="termsAndConditions"
            value={[data.hasAccepted]}
            onChange={handleOnTermsCheckboxValueChange}
          >
            <Checkbox
              id={'confirm-step-4'}
              value={'agree'}
              variant={'xl'}
              label={'I agree to the following'}
            />
          </CheckboxGroup>
          <div className={'mt-8'}>
            <div
              className={
                'pb-3 fw-light lh-xs border-bottom border-info text-fs-6'
              }
            >
              {
                'I certify the information I have provided is true and correct and that I am responsible for updating this information in the event it changes.'
              }
            </div>
            <div
              className={
                'py-3 fw-light lh-xs border-bottom border-info text-fs-6'
              }
            >
              {
                "I acknowledge that falsifying this information or failing to update this information can lead to cancellation of my dependent's coverage and disciplinary action up to and including termination of employment."
              }
            </div>
            <div
              className={
                'py-3 fw-light lh-xs border-bottom border-info text-fs-6'
              }
            >
              {
                'I understand that submission of this form does not guarantee eligibility for benefits.'
              }
            </div>
            <div
              className={
                'py-3 fw-light lh-xs border-bottom border-info text-fs-6'
              }
            >
              {
                'I certify the information I have provided is true and correct and that I am responsible for updating this information in the event it changes.'
              }
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default VerifyDependentStep4;
